<script setup>
import { ref, computed, reactive, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import AgentBotsAPI from '../../../../../api/agentBots';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { required, helpers } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
// import { useToggle } from '@vueuse/core';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
// Access token UI removed for CSML modal
import CSMLMonacoEditor from './CSMLMonacoEditor.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedBot: {
    type: Object,
    default: () => ({}),
  },
});

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const uiFlags = useMapGetter('agentBots/getUIFlags');

const formState = reactive({
  botName: '',
  botDescription: '',
  csmlContent: '',
  botAvatar: null,
  botAvatarUrl: '',
});

// Access token toggles removed from CSML modal

const v$ = useVuelidate(
  {
    botName: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.NAME'),
        required
      ),
    },
    csmlContent: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.CSML_BOT_EDITOR.BOT_CONFIG.ERROR'),
        required
      ),
    },
  },
  formState
);

const isLoading = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? uiFlags.value.isCreating
    : uiFlags.value.isUpdating
);

const dialogTitle = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.CSML_BOT.CREATE')
    : t('AGENT_BOTS.CSML_BOT.UPDATE')
);

const dialogDescription = computed(() => '');

const confirmButtonLabel = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.CSML_BOT.CREATE')
    : t('AGENT_BOTS.CSML_BOT.UPDATE')
);

const botNameError = computed(() =>
  v$.value.botName.$error ? v$.value.botName.$errors[0]?.$message : ''
);

const csmlContentError = computed(() =>
  v$.value.csmlContent.$error ? v$.value.csmlContent.$errors[0]?.$message : ''
);

// Access token block removed; no computed needed

const resetForm = () => {
  Object.assign(formState, {
    botName: '',
    botDescription: '',
    csmlContent: '',
    botAvatar: null,
    botAvatarUrl: '',
  });
  v$.value.$reset();
};

const handleImageUpload = ({ file, url: avatarUrl }) => {
  formState.botAvatar = file;
  formState.botAvatarUrl = avatarUrl;
};

const handleAvatarDelete = async () => {
  if (props.selectedBot?.id) {
    try {
      await store.dispatch(
        'agentBots/deleteAgentBotAvatar',
        props.selectedBot.id
      );
      formState.botAvatar = null;
      formState.botAvatarUrl = '';
      useAlert(t('AGENT_BOTS.AVATAR.SUCCESS_DELETE'));
    } catch (error) {
      useAlert(t('AGENT_BOTS.AVATAR.ERROR_DELETE'));
    }
  } else {
    formState.botAvatar = null;
    formState.botAvatarUrl = '';
  }
};

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const botData = {
    name: formState.botName,
    description: formState.botDescription,
    bot_type: 'csml',
    outgoing_url: '',
    avatar: formState.botAvatar,
    bot_config: {
      csml_content: formState.csmlContent,
    },
  };

  const isCreate = props.type === MODAL_TYPES.CREATE;

  try {
    const actionPayload = isCreate
      ? botData
      : { id: props.selectedBot.id, data: botData };

    await store.dispatch(
      `agentBots/${isCreate ? 'create' : 'update'}`,
      actionPayload
    );

    const alertKey = isCreate
      ? t('AGENT_BOTS.ADD.API.SUCCESS_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.SUCCESS_MESSAGE');
    useAlert(alertKey);

    dialogRef.value.close();

    resetForm();
  } catch (error) {
    const errorKey = isCreate
      ? t('AGENT_BOTS.ADD.API.ERROR_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.ERROR_MESSAGE');
    useAlert(errorKey);
  }
};

const initializeForm = () => {
  if (props.selectedBot && Object.keys(props.selectedBot).length) {
    const {
      name,
      description,
      bot_config: botConfig,
      thumbnail,
    } = props.selectedBot;
    formState.botName = name || '';
    formState.botDescription = description || '';
    formState.csmlContent = botConfig?.csml_content || '';
    formState.botAvatarUrl = thumbnail || '';

    // if (botAccessToken && props.type === MODAL_TYPES.EDIT) {
    //   accessToken.value = botAccessToken;
    // }
  } else {
    resetForm();
  }

  // Add default CSML content for new bots
  if (props.type === MODAL_TYPES.CREATE && !formState.csmlContent) {
    formState.csmlContent = `start:
    say "Hello! I am an Etisalna Flow bot. How can I help you today?"`;
  }
  // debug logs removed
};

// Ensure full bot data is loaded when editing a CSML bot
const ensureFullBotData = async () => {
  if (
    props.type === MODAL_TYPES.EDIT &&
    props.selectedBot?.id &&
    props.selectedBot?.bot_type === 'csml' &&
    (!props.selectedBot.bot_config ||
      typeof props.selectedBot.bot_config.csml_content === 'undefined')
  ) {
    try {
      await store.dispatch('agentBots/show', props.selectedBot.id);
      // Retrieve the refreshed bot from store
      const fullBot = store.getters['agentBots/getBot'](props.selectedBot.id);
      // debug logs removed
      let nextContent = fullBot?.bot_config?.csml_content;
      // Fallback: direct AJAX if store didn’t bring csml_content
      if (!nextContent) {
        try {
          const { data } = await AgentBotsAPI.show(props.selectedBot.id);
          // debug logs removed
          nextContent = data?.bot_config?.csml_content || '';
        } catch (_) {
          // ignore
        }
      }
      formState.csmlContent = nextContent || '';
    } catch (e) {
      // ignore fetch errors; user can still edit
    }
  }
};

// Access token logic removed for CSML modal

const closeModal = () => {
  v$.value?.$reset();
};

const onClickClose = () => {
  closeModal();
  dialogRef.value.close();
};

watch(
  () => props.selectedBot,
  async () => {
    initializeForm();
    await ensureFullBotData();
  },
  { immediate: true, deep: true }
);

// removed csmlContent debug watcher

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="dialogTitle"
    :description="dialogDescription"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="full"
    @close="closeModal"
  >
    <form class="flex flex-row gap-4" @submit.prevent="handleSubmit">
      <!-- Left: CSML Editor (80%) -->
      <div class="w-[80%]">
        <div
          class="h-[80vh] border border-n-slate-7 rounded-md overflow-hidden"
        >
          <CSMLMonacoEditor
            v-model="formState.csmlContent"
            class="w-full h-full"
          />
        </div>
        <div v-if="csmlContentError" class="mt-2 text-sm text-red-600">
          {{ csmlContentError }}
        </div>
      </div>

      <!-- Right: Sidebar (20%) -->
      <div class="w-[20%] flex flex-col gap-4">
        <div class="flex flex-col items-start">
          <span class="mb-2 text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_BOTS.FORM.AVATAR.LABEL') }}
          </span>
          <Avatar
            :src="formState.botAvatarUrl"
            :name="formState.botName"
            :size="68"
            allow-upload
            icon-name="i-lucide-bot-message-square"
            @upload="handleImageUpload"
            @delete="handleAvatarDelete"
          />
        </div>

        <Input
          id="csml-modal-bot-name"
          v-model="formState.botName"
          :label="$t('AGENT_BOTS.FORM.NAME.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.NAME.PLACEHOLDER')"
          :message="botNameError"
          :message-type="botNameError ? 'error' : 'info'"
          @blur="v$.botName.$touch()"
        />

        <TextArea
          id="csml-modal-bot-description"
          v-model="formState.botDescription"
          :label="$t('AGENT_BOTS.FORM.DESCRIPTION.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.DESCRIPTION.PLACEHOLDER')"
        />

        <div class="mt-auto flex items-center justify-end gap-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('AGENT_BOTS.FORM.CANCEL')"
            @click="onClickClose()"
          />
          <NextButton
            type="submit"
            data-testid="csml-bot-submit"
            :label="confirmButtonLabel"
            :is-loading="isLoading"
            :disabled="v$.$invalid"
          />
        </div>
      </div>
    </form>
  </Dialog>
</template>
